import { createStore } from '@xstate/store';
import type { SkillDefinition } from '../skills';
import { SKILL_DEFINITIONS as DEFAULT_SKILLS } from '../skills';

// ============================================
// Skills Store - Verwaltet die Skill-Definitionen
// ============================================

const SKILLS_LOCAL_STORAGE_KEY = 'roadmap_2026_skills';
const PERSISTENCE_DEBOUNCE_MS = 500;

export interface SkillsContext {
  skills: SkillDefinition[];
}

// Lade Skills aus LocalStorage
function loadSkillsFromLocalStorage(): SkillDefinition[] {
  try {
    const saved = localStorage.getItem(SKILLS_LOCAL_STORAGE_KEY);
    if (!saved) return [...DEFAULT_SKILLS];

    const parsed = JSON.parse(saved) as SkillDefinition[];
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return [...DEFAULT_SKILLS];
    }
    return parsed;
  } catch {
    return [...DEFAULT_SKILLS];
  }
}

// Speichere Skills in LocalStorage
function saveSkillsToLocalStorage(skills: SkillDefinition[]): void {
  localStorage.setItem(SKILLS_LOCAL_STORAGE_KEY, JSON.stringify(skills));
}

const initialContext: SkillsContext = {
  skills: loadSkillsFromLocalStorage(),
};

export const skillsStore = createStore({
  context: initialContext,

  on: {
    'skill.add': (ctx, event: { skill: SkillDefinition }) => {
      // Prüfe ob ID bereits existiert
      if (ctx.skills.some(s => s.id === event.skill.id)) {
        console.warn(`[skillsStore] Skill with ID "${event.skill.id}" already exists`);
        return ctx;
      }
      // Prüfe ob Emoji bereits existiert
      if (ctx.skills.some(s => s.emoji === event.skill.emoji)) {
        console.warn(`[skillsStore] Skill with emoji "${event.skill.emoji}" already exists`);
        return ctx;
      }
      return {
        ...ctx,
        skills: [...ctx.skills, event.skill],
      };
    },

    'skill.update': (ctx, event: { skillId: string; updates: Partial<Omit<SkillDefinition, 'id'>> }) => {
      const skillIndex = ctx.skills.findIndex(s => s.id === event.skillId);
      if (skillIndex === -1) {
        console.warn(`[skillsStore] Skill with ID "${event.skillId}" not found`);
        return ctx;
      }

      // Prüfe ob neues Emoji bereits von einem anderen Skill verwendet wird
      if (event.updates.emoji) {
        const existingWithEmoji = ctx.skills.find(s => s.emoji === event.updates.emoji && s.id !== event.skillId);
        if (existingWithEmoji) {
          console.warn(`[skillsStore] Emoji "${event.updates.emoji}" is already used by skill "${existingWithEmoji.id}"`);
          return ctx;
        }
      }

      const updatedSkills = [...ctx.skills];
      updatedSkills[skillIndex] = {
        ...updatedSkills[skillIndex],
        ...event.updates,
      };

      return {
        ...ctx,
        skills: updatedSkills,
      };
    },

    'skill.delete': (ctx, event: { skillId: string }) => {
      const skillIndex = ctx.skills.findIndex(s => s.id === event.skillId);
      if (skillIndex === -1) {
        console.warn(`[skillsStore] Skill with ID "${event.skillId}" not found`);
        return ctx;
      }
      return {
        ...ctx,
        skills: ctx.skills.filter(s => s.id !== event.skillId),
      };
    },

    'skills.reset': () => ({
      skills: [...DEFAULT_SKILLS],
    }),

    'skills.import': (ctx, event: { skills: SkillDefinition[] }) => ({
      ...ctx,
      skills: event.skills,
    }),
  },
});

// ============================================
// Side Effects: Debounced Persistence
// ============================================

let saveTimeout: ReturnType<typeof setTimeout> | null = null;
let lastSkillsHash = '';

function computeSkillsHash(skills: SkillDefinition[]): string {
  return skills.map(s => `${s.id}:${s.emoji}:${s.name}`).join('|');
}

// Subscribe to changes and persist
skillsStore.subscribe((snapshot) => {
  const currentHash = computeSkillsHash(snapshot.context.skills);

  if (currentHash === lastSkillsHash) return;
  lastSkillsHash = currentHash;

  // Debounced persistence
  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    saveSkillsToLocalStorage(snapshot.context.skills);
  }, PERSISTENCE_DEBOUNCE_MS);
});

// ============================================
// Helper-Funktionen
// ============================================

export function getSkillsArray(): SkillDefinition[] {
  return skillsStore.getSnapshot().context.skills;
}

export function getSkillById(skillId: string): SkillDefinition | undefined {
  return skillsStore.getSnapshot().context.skills.find(s => s.id === skillId);
}

export function getSkillByEmoji(emoji: string): SkillDefinition | undefined {
  return skillsStore.getSnapshot().context.skills.find(s => s.emoji === emoji);
}

// Generiert das EMOJI_TO_SKILL Mapping dynamisch
export function getEmojiToSkillMap(): Record<string, string> {
  const skills = getSkillsArray();
  return Object.fromEntries(skills.map(s => [s.emoji, s.id]));
}

// Generiert das SKILL_TO_EMOJI Mapping dynamisch
export function getSkillToEmojiMap(): Record<string, string> {
  const skills = getSkillsArray();
  return Object.fromEntries(skills.map(s => [s.id, s.emoji]));
}

// Generiert eine neue eindeutige Skill-ID
export function generateSkillId(baseName: string): string {
  const baseId = baseName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const existingSkills = getSkillsArray();
  let id = baseId || 'new_skill';
  let counter = 1;

  while (existingSkills.some(s => s.id === id)) {
    id = `${baseId}_${counter}`;
    counter++;
  }

  return id;
}

export type SkillsStore = typeof skillsStore;
